import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import html from "../assets/html.png";
import css from "../assets/css.png";
import reactImage from "../assets/react.png";
import c from "../assets/c.jpeg";
import java from "../assets/java.png";
import github from "../assets/github.png";
import tailwind from "../assets/tailwind.png";
import javascript from "../assets/javascript.png";
import mysql from "../assets/mysql.png";
import cplus from "../assets/cplus.jpg";
import springboot from "../assets/springboot.png";
import nodejs from "../assets/node.png";
import express from "../assets/express.png";
import mongodb from "../assets/mongodb.png";
import firebase from "../assets/firebase.png";
import python from "../assets/python.png";
import flask from "../assets/flask.png";
import figma from "../assets/figma.png";
import jira from "../assets/jira.png";
import reactNative from "../assets/reactnative.png";
import {
  SiTypescript,
  SiNextdotjs,
  SiRedux,
  SiFastify,
  SiNestjs,
  SiGraphql,
  SiPhp,
  SiNetlify,
  SiVisualstudiocode,
} from "react-icons/si";
import SectionHeader from "./SectionHeader";
import { fadeUpItem, staggerFast, viewportOnce } from "../motionPresets";

const Experience = () => {
  const reduced = useReducedMotion();
  const techs = [
    { id: 1, src: html, title: "HTML", category: "frontend" },
    { id: 2, src: css, title: "CSS", category: "frontend" },
    { id: 3, src: javascript, title: "JavaScript", category: "frontend" },
    {
      id: 4,
      Icon: SiTypescript,
      title: "TypeScript",
      category: "frontend",
      iconClass: "text-[#3178C6]",
    },
    { id: 5, src: reactImage, title: "React", category: "frontend" },
    { id: 6, src: reactNative, title: "React Native", category: "frontend" },
    { id: 7, src: tailwind, title: "Tailwind CSS", category: "frontend" },
    {
      id: 8,
      Icon: SiNextdotjs,
      title: "Next.js",
      category: "frontend",
      iconClass: "text-white",
    },
    {
      id: 9,
      Icon: SiRedux,
      title: "Redux",
      category: "frontend",
      iconClass: "text-[#764ABC]",
    },

    { id: 10, src: nodejs, title: "Node.js", category: "backend" },
    { id: 11, src: express, title: "Express.js", category: "backend" },
    {
      id: 12,
      Icon: SiFastify,
      title: "Fastify",
      category: "backend",
      iconClass: "text-white",
    },
    {
      id: 13,
      Icon: SiNestjs,
      title: "NestJS",
      category: "backend",
      iconClass: "text-[#E0234E]",
    },
    { id: 14, src: java, title: "Java", category: "backend" },
    { id: 15, src: springboot, title: "Spring Boot", category: "backend" },
    {
      id: 16,
      Icon: SiGraphql,
      title: "GraphQL",
      category: "backend",
      iconClass: "text-[#E10098]",
    },
    {
      id: 17,
      Icon: SiPhp,
      title: "PHP",
      category: "backend",
      iconClass: "text-[#777BB4]",
    },
    { id: 18, src: python, title: "Python", category: "backend" },
    { id: 19, src: flask, title: "Flask", category: "backend" },
    { id: 20, src: c, title: "C", category: "backend" },
    { id: 21, src: cplus, title: "C++", category: "backend" },

    { id: 22, src: mysql, title: "MySQL", category: "database" },
    { id: 23, src: mongodb, title: "MongoDB", category: "database" },
    { id: 24, src: firebase, title: "Firebase", category: "database" },

    { id: 25, src: github, title: "GitHub", category: "tools" },
    { id: 26, src: figma, title: "Figma", category: "tools" },
    { id: 27, src: jira, title: "JIRA", category: "tools" },
    {
      id: 28,
      Icon: SiNetlify,
      title: "Netlify",
      category: "tools",
      iconClass: "text-[#00C7B7]",
    },
    {
      id: 29,
      Icon: SiVisualstudiocode,
      title: "VS Code",
      category: "tools",
      iconClass: "text-[#007ACC]",
    },
  ];

  const categories = [
    { name: "Frontend", id: "frontend" },
    { name: "Backend & APIs", id: "backend" },
    { name: "Databases", id: "database" },
    { name: "Tools & platforms", id: "tools" },
  ];

  return (
    <div
      name="experience"
      className="w-full border-t border-white/[0.06] bg-[#030712] text-white pt-20 pb-20 px-4 sm:px-6"
    >
      <div className="max-w-6xl mx-auto">
        <SectionHeader
          eyebrow="Capabilities"
          title="Technical skills"
          subtitle="Languages, frameworks, and tooling I use to ship full-stack products end to end."
        />

        {categories.map((category) => (
          <motion.div
            key={category.id}
            className="mb-12"
            variants={staggerFast}
            initial={reduced ? false : "hidden"}
            animate={reduced ? "visible" : undefined}
            whileInView={reduced ? undefined : "visible"}
            viewport={viewportOnce}
          >
            <h3 className="text-sm font-semibold mb-5 text-sky-400/95 uppercase tracking-[0.12em] border-b border-white/[0.06] pb-3">
              {category.name}
            </h3>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3 sm:gap-4">
              {techs
                .filter((tech) => tech.category === category.id)
                .map((tech) => (
                  <motion.div
                    key={tech.id}
                    variants={fadeUpItem}
                    whileHover={reduced ? undefined : { scale: 1.03 }}
                    transition={{ duration: 0.2 }}
                    className="rounded-2xl border border-white/[0.08] bg-slate-900/30 backdrop-blur-sm p-5 flex flex-col items-center hover:border-sky-500/25 hover:bg-sky-500/[0.04] transition-colors duration-300 shadow-md shadow-black/20 ring-1 ring-white/[0.03]"
                  >
                    {tech.Icon ? (
                      <tech.Icon className={`w-14 h-14 mb-3 ${tech.iconClass || ""}`} />
                    ) : (
                      <img
                        src={tech.src}
                        alt={tech.title}
                        className="w-14 h-14 object-contain mb-3"
                      />
                    )}
                    <p className="text-slate-200 font-medium text-center text-sm">{tech.title}</p>
                  </motion.div>
                ))}
            </div>
          </motion.div>
        ))}

        <motion.div
          className="rounded-2xl border border-white/[0.08] bg-slate-900/35 p-6 md:p-8 ring-1 ring-white/[0.04]"
          initial={reduced ? false : { opacity: 0, y: 20 }}
          whileInView={reduced ? undefined : { opacity: 1, y: 0 }}
          viewport={viewportOnce}
          transition={{ duration: reduced ? 0 : 0.45, ease: [0.22, 1, 0.36, 1] }}
        >
          <h3 className="text-sm font-semibold mb-4 text-sky-400 uppercase tracking-[0.12em]">Practices</h3>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            {["REST APIs", "JWT", "Git workflows", "Agile / Scrum", "PRINCE2"].map((label) => (
              <div
                key={label}
                className="rounded-xl bg-slate-950/60 border border-white/[0.06] py-3 px-2 text-center text-slate-300 text-sm font-medium"
              >
                {label}
              </div>
            ))}
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default Experience;
